import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { apiFetch } from "../api";
import "./Settings.css";

const Settings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [patterns, setPatterns] = useState([]);
  const [roasters, setRoasters] = useState([]);
  const [defaultRoasterId, setDefaultRoasterId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingRoasterId, setEditingRoasterId] = useState(null);
  const [editingRoasterName, setEditingRoasterName] = useState("");
  const [activeMenu, setActiveMenu] = useState(null);
  const [newRoasterName, setNewRoasterName] = useState("");
  const [addingRoaster, setAddingRoaster] = useState(false);

  // Fetch existing patterns and default roaster on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [patternsRes, roastersRes, defaultRoasterRes] = await Promise.all([
          apiFetch("/auth/me/batch-patterns"),
          apiFetch("/roasters"),
          apiFetch("/auth/me/default-roaster"),
        ]);

        if (!patternsRes.ok || !roastersRes.ok || !defaultRoasterRes.ok) {
          throw new Error("Failed to fetch settings data");
        }

        const patternsData = await patternsRes.json();
        const roastersData = await roastersRes.json();
        const defaultRoasterData = await defaultRoasterRes.json();

        setPatterns(patternsData.patterns || []);
        setRoasters(roastersData || []);
        setDefaultRoasterId(defaultRoasterData.default_roaster_id || null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeMenu && !e.target.closest(".roaster-menu")) {
        setActiveMenu(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [activeMenu]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleAddPattern = () => {
    setPatterns([
      ...patterns,
      { pattern: "", examples: [""], description: "" },
    ]);
  };

  const handleRemovePattern = (index) => {
    setPatterns(patterns.filter((_, i) => i !== index));
  };

  const handlePatternChange = (index, field, value) => {
    const updated = [...patterns];
    updated[index] = { ...updated[index], [field]: value };
    setPatterns(updated);
  };

  const handleAddExample = (patternIndex) => {
    const updated = [...patterns];
    updated[patternIndex].examples = [...updated[patternIndex].examples, ""];
    setPatterns(updated);
  };

  const handleRemoveExample = (patternIndex, exampleIndex) => {
    const updated = [...patterns];
    updated[patternIndex].examples = updated[patternIndex].examples.filter(
      (_, i) => i !== exampleIndex,
    );
    setPatterns(updated);
  };

  const handleExampleChange = (patternIndex, exampleIndex, value) => {
    const updated = [...patterns];
    updated[patternIndex].examples[exampleIndex] = value;
    setPatterns(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    // Filter out empty patterns and examples
    const cleanedPatterns = patterns
      .filter((p) => p.examples.some((ex) => ex.trim()))
      .map((p) => ({
        ...p,
        examples: p.examples.filter((ex) => ex.trim()),
      }));

    try {
      const res = await apiFetch("/auth/me/batch-patterns", {
        method: "PUT",
        body: JSON.stringify({ patterns: cleanedPatterns }),
      });

      if (!res.ok) {
        throw new Error("Failed to save settings");
      }

      const patternsData = await res.json();
      setPatterns(patternsData.patterns);
      setSuccess("Settings saved successfully!");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefaultRoaster = async (roasterId) => {
    setError("");
    setActiveMenu(null);

    try {
      const res = await apiFetch("/auth/me/default-roaster", {
        method: "PUT",
        body: JSON.stringify({ default_roaster_id: roasterId }),
      });

      if (!res.ok) {
        throw new Error("Failed to set default roaster");
      }

      setDefaultRoasterId(roasterId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditRoaster = (roaster) => {
    setEditingRoasterId(roaster.id);
    setEditingRoasterName(roaster.name);
    setActiveMenu(null);
  };

  const handleSaveRoasterName = async (roasterId) => {
    if (!editingRoasterName.trim()) {
      setError("Roaster name cannot be empty");
      return;
    }

    setError("");

    try {
      const res = await apiFetch(`/roasters/${roasterId}`, {
        method: "PATCH",
        body: JSON.stringify({ name: editingRoasterName.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to update roaster name");
      }

      const updatedRoaster = await res.json();
      setRoasters(
        roasters.map((r) => (r.id === roasterId ? updatedRoaster : r)),
      );
      setEditingRoasterId(null);
      setEditingRoasterName("");
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingRoasterId(null);
    setEditingRoasterName("");
  };

  const handleDeleteRoaster = async (roasterId) => {
    if (!window.confirm("Are you sure you want to delete this roaster?")) {
      return;
    }

    setError("");
    setActiveMenu(null);

    try {
      const res = await apiFetch(`/roasters/${roasterId}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(
          data.detail || "Cannot delete - roaster has associated batches",
        );
      }

      setRoasters(roasters.filter((r) => r.id !== roasterId));
      if (defaultRoasterId === roasterId) {
        setDefaultRoasterId(null);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleMenu = (roasterId) => {
    setActiveMenu(activeMenu === roasterId ? null : roasterId);
  };

  const handleAddRoaster = async () => {
    if (!newRoasterName.trim()) {
      setError("Roaster name cannot be empty");
      return;
    }

    setError("");

    try {
      const res = await apiFetch("/roasters", {
        method: "POST",
        body: JSON.stringify({ name: newRoasterName.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to create roaster");
      }

      const newRoaster = await res.json();
      setRoasters([...roasters, newRoaster]);
      setNewRoasterName("");
      setAddingRoaster(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancelAddRoaster = () => {
    setNewRoasterName("");
    setAddingRoaster(false);
  };

  if (loading) {
    return (
      <div className="sessions-page">
        <Header user={user} onLogout={handleLogout} />
        <main className="sessions-content">
          <div className="loading-message">Loading settings...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Header user={user} onLogout={handleLogout} />
      <main className="dashboard-content">
        <div className="settings-header">
          <h2>Settings</h2>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <section className="settings-section">
          <div className="section-header">
            <h3>My Roasters</h3>
            <p className="section-description">
              View all your saved roasters. Roasters are automatically created
              when you record cupping sessions.
            </p>
          </div>

          <div className="roasters-list">
            {roasters.length === 0 ? (
              <div className="empty-roasters">
                <p>No roasters yet.</p>
                <p className="hint">
                  Roasters are automatically created when you mention them in
                  your cupping notes. Start by{" "}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => navigate("/sessions")}
                  >
                    recording a session
                  </button>
                  .
                </p>
              </div>
            ) : (
              <ul className="roaster-items">
                {roasters.map((roaster) => (
                  <li key={roaster.id} className="roaster-item">
                    {editingRoasterId === roaster.id ? (
                      <>
                        <input
                          type="text"
                          className="roaster-name-input"
                          value={editingRoasterName}
                          onChange={(e) => setEditingRoasterName(e.target.value)}
                          autoFocus
                        />
                        <div className="roaster-edit-actions">
                          <button
                            type="button"
                            className="btn-confirm-edit"
                            onClick={() => handleSaveRoasterName(roaster.id)}
                            title="Save"
                          >
                            ✓
                          </button>
                          <button
                            type="button"
                            className="btn-cancel-edit"
                            onClick={handleCancelEdit}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="roaster-info">
                          <span className="roaster-name">{roaster.name}</span>
                          {defaultRoasterId === roaster.id && (
                            <span className="default-badge">default</span>
                          )}
                        </div>
                        <div className="roaster-menu">
                          <button
                            type="button"
                            className="btn-menu"
                            onClick={() => toggleMenu(roaster.id)}
                            title="Options"
                          >
                            ⋮
                          </button>
                          {activeMenu === roaster.id && (
                            <div className="menu-dropdown">
                              <button
                                type="button"
                                onClick={() => handleSetDefaultRoaster(roaster.id)}
                              >
                                Set as default
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEditRoaster(roaster)}
                              >
                                Edit name
                              </button>
                              <button
                                type="button"
                                className="danger"
                                onClick={() => handleDeleteRoaster(roaster.id)}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {addingRoaster && (
              <div className="new-roaster-form">
                <input
                  type="text"
                  className="roaster-name-input"
                  placeholder="Enter roaster name"
                  value={newRoasterName}
                  onChange={(e) => setNewRoasterName(e.target.value)}
                  autoFocus
                />
                <div className="roaster-edit-actions">
                  <button
                    type="button"
                    className="btn-confirm-edit"
                    onClick={handleAddRoaster}
                    title="Save"
                  >
                    ✓
                  </button>
                  <button
                    type="button"
                    className="btn-cancel-edit"
                    onClick={handleCancelAddRoaster}
                    title="Cancel"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            <button
              type="button"
              className="btn-add-pattern"
              onClick={() => setAddingRoaster(true)}
              disabled={addingRoaster}
            >
              + Add Roaster
            </button>
          </div>
        </section>

        <section className="settings-section">
          <div className="section-header">
            <h3>Batch ID Patterns</h3>
            <p className="section-description">
              Help us recognize your batch IDs by providing examples. When you
              record cupping notes, we'll look for IDs matching these patterns.
            </p>
          </div>

          <div className="patterns-list">
            {patterns.length === 0 ? (
              <div className="empty-patterns">
                <p>No patterns defined yet.</p>
                <p className="hint">
                  Add your first pattern to help identify batch IDs like "GB-1",
                  "LOT-47", or "CED-482".
                </p>
              </div>
            ) : (
              patterns.map((pattern, patternIndex) => (
                <div key={patternIndex} className="pattern-card">
                  <div className="pattern-header">
                    <span className="pattern-number">
                      Pattern {patternIndex + 1}
                    </span>
                    <button
                      type="button"
                      className="btn-remove-pattern"
                      onClick={() => handleRemovePattern(patternIndex)}
                      title="Remove pattern"
                    >
                      ×
                    </button>
                  </div>

                  <div className="pattern-field">
                    <label>Description (optional)</label>
                    <input
                      type="text"
                      placeholder="e.g., Green bean lots, Roast batches"
                      value={pattern.description || ""}
                      onChange={(e) =>
                        handlePatternChange(
                          patternIndex,
                          "description",
                          e.target.value,
                        )
                      }
                    />
                  </div>

                  <div className="pattern-field">
                    <label>
                      Examples <span className="required">*</span>
                    </label>
                    <p className="field-hint">
                      Add real batch IDs you use. The more examples, the better
                      we can recognize them.
                    </p>
                    <div className="examples-list">
                      {pattern.examples.map((example, exampleIndex) => (
                        <div key={exampleIndex} className="example-row">
                          <input
                            type="text"
                            placeholder="e.g., GB-1, LOT-47"
                            value={example}
                            onChange={(e) =>
                              handleExampleChange(
                                patternIndex,
                                exampleIndex,
                                e.target.value,
                              )
                            }
                          />
                          {pattern.examples.length > 1 && (
                            <button
                              type="button"
                              className="btn-remove-example"
                              onClick={() =>
                                handleRemoveExample(patternIndex, exampleIndex)
                              }
                              title="Remove example"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="btn-add-example"
                      onClick={() => handleAddExample(patternIndex)}
                    >
                      + Add another example
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <button
            type="button"
            className="btn-add-pattern"
            onClick={handleAddPattern}
          >
            + Add Batch ID Pattern
          </button>
        </section>

        <div className="settings-actions">
          <button
            type="button"
            className="btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </main>
    </div>
  );
};

export default Settings;
